// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {AuthService} from 'src/services/auth-service';
import {NotificationService} from 'src/services/notification-service';
import {PasswordResetStatus, UserService} from 'src/services/user-service';
import {CustomValidators} from 'src/app/shared/validators/custom-validators';
import {IManageUser, IUser, LoginStatus, UserSource} from 'src/types/user';
import {Router} from '@angular/router';
import {IPasswordComponent, IPasswordComponentHelper} from '../../../../types/password';
import {switchMap} from 'rxjs/operators';
import authHelper from '../../../auth/authHelper';
import {Observable, of} from 'rxjs';
import {Store} from '@ngrx/store';
import {AppState} from '../../../store/app.state';
import {selectUser} from '../../../store/user/user.selectors';
import {MatTooltip} from '@angular/material/tooltip';

@Component({
    selector: 'app-password',
    templateUrl: './password.component.html',
    styleUrls: ['./password.component.scss'],
    standalone: false
})
export class PasswordComponent implements OnInit, IPasswordComponent {
  @ViewChild('tooltipNew') tooltipNew: MatTooltip;
  @ViewChild('tooltipCurrent') tooltipCurrent: MatTooltip;
  @ViewChild('tooltipConfirm') tooltipConfirm: MatTooltip;
  private _mode: string;
  private _validModes = ['UPDATE', 'RESET', 'INVITATION', 'DEVICE_UPDATE'];

  user$: Observable<IManageUser> = this.store.select(selectUser());

  @Input() stayLoggedIn: boolean = false;
  @Input() deviceToken: string;
  @Input() showBestPractices: boolean;
  @Input() username: string;
  @Input() password: string;
  @Input() secondFactor: string;
  @Input() email: string;
  @Input() resetCode: string;
  @Input() isEmailVerified: boolean;
  @Input() invitationUUID: string;
  @Input()
  get mode(): string { return this._mode; }
  set mode(mode: string) {
    mode = mode && mode.toUpperCase() || 'UPDATE';
    this._mode = this._validModes.includes(mode) ? mode : 'UPDATE';
    switch (this._mode) {
      case 'RESET':
        this.showCurrentPassword = false;
        break;
      case 'INVITATION':
        this.showCurrentPassword = false;
        break;
      case 'DEVICE_UPDATE':
        this.showCurrentPassword = false;
        break;
      default:
        this.showCurrentPassword = true;
        break;
    }
  }

  showCurrentPassword: boolean;
  passwordForm: UntypedFormGroup;
  showCurrentPass: boolean;
  showNewPass: boolean;
  submittedPasswd: boolean;
  forcePassword: boolean;

  hasLowerCase = false;
  hasUpperCase = false;
  hasUnicode = false;
  hasNumber = false;
  hasSpecial = false;
  categoriesSatisfied = 0;
  capsOnCurrent: boolean = false;
  capsOnNew: boolean = false;
  capsOnConfirm: boolean = false;
  passwordCurrentInputIsFocused: boolean;
  passwordInputIsFocused: boolean;
  passwordConfirmInputIsFocused: boolean;


  tlMustHave1: string;
  tlMustHave2: string;
  tlMustHave3: string;
  tlMustHave4: string;
  tlUppercase: string;
  tlLowercase: string;
  tlLetter: string;
  tlUnicode: string;
  tlUnicode2: string;
  tlNumber: string;
  tlSpecialCharacter: string;
  tlCurrentPassword: string;
  tlCurrentIsReq: string;
  tlNewPassword: string;
  tlPwdRequired: string;
  tlErrorCategories: string;
  tlErrorLen: string;
  tlErrorSimCurrent: string;
  tlErrorSimEmail: string;
  tlConfirmNewPwd: string;
  tlConfirmReq: string;
  tlErrorConfirmMustMatch: string;
  tlBestPractices: string;
  tlUse: string;
  tlUsePasswordMgr: string;
  tlUseUniquePwd: string;
  tlUseCase: string;
  tlUseLongLen: string;
  tlDoNotUse: string;
  tlDoNotRepeat: string;
  tlDoNotUseUsernameEmail: string;
  tlDoNotUsePersonal: string;
  tlAvoid: string;
  tlAvoidWriting: string;
  tlAvoidSharing: string;
  tlAVoidSaving: string;
  tlErrorProfile: string;
  tlErrorRecent: string;
  tlUnknownError: string;
  tlErrorContainsPipe: string;

  constructor(
    private notification: NotificationService,
    private userService: UserService,
    private translate: TranslateService,
    private authService: AuthService,
    private formBuilder: UntypedFormBuilder,
    public router: Router,
    private readonly store: Store<AppState>,
    ) {
      this.mode = 'UPDATE';
      this.showCurrentPass = false;
      this.showNewPass = false;
      this.submittedPasswd = false;
      this.forcePassword = false;
      this.showCurrentPassword = true;
      this.isEmailVerified = false;
      this.showBestPractices = true;
      this.invitationUUID = '';

      const keys = ['PW_CURRENT_PWD', 'PW_CURRENT_IS_REQ', 'PW_NEW_PWD', 'SUB_P_REQ',
                    'PW_ERROR_CAT', 'SUB_P_LEN', 'PW_ERROR_SIM_PWD', 'PW_ERROR_SIM_EMAIL', 'PW_CONFIRM_NEW',
                    'SUB_CONFIRM_REQ', 'SUB_PASS_MATCH', 'PW_USE', 'ERROR_OCCURRED_UNKNOWN',
                    'PW_BEST_PRACTICES', 'PW_USE_PWD_MGR', 'PW_USE_UNIQUE', 'PW_USE_CASE', 'PW_USE_LONG_LEN',
                    'PW_DO_NOT_USE', 'PW_DO_NOT_REPEAT', 'PW_DO_NOT_USE_USER_EMAIL', 'PW_DO_NOT_USE_PERSONAL',
                    'PW_AVOID', 'PW_AVOID_WRITING', 'PW_AVOID_SHARING', 'PW_AVOID_SAVING',
                    'PW_ERROR_PROFILE', 'PW_ERROR_RECENT', 'PW_ERROR_CONTAINS_PIPE'];

      IPasswordComponentHelper.addTranslationKeys(keys);

      this.translate.get(keys)
          .subscribe(vals => {
            IPasswordComponentHelper.setTranslationValues(this, vals);
            this.tlCurrentPassword = vals['PW_CURRENT_PWD'];
            this.tlCurrentIsReq = vals['PW_CURRENT_IS_REQ'];
            this.tlNewPassword = vals['PW_NEW_PWD'];
            this.tlPwdRequired = vals['SUB_P_REQ'];
            this.tlErrorCategories = vals['PW_ERROR_CAT'];
            this.tlErrorLen = vals['SUB_P_LEN'];
            this.tlErrorSimCurrent = vals['PW_ERROR_SIM_PWD'];
            this.tlErrorSimEmail = vals['PW_ERROR_SIM_EMAIL'];
            this.tlErrorContainsPipe = vals['PW_ERROR_CONTAINS_PIPE'];
            this.tlConfirmNewPwd = vals['PW_CONFIRM_NEW'];
            this.tlConfirmReq = vals['SUB_CONFIRM_REQ'];
            this.tlErrorConfirmMustMatch = vals['SUB_PASS_MATCH'];
            this.tlBestPractices = vals['PW_BEST_PRACTICES'];
            this.tlUse = vals['PW_USE'];
            this.tlUsePasswordMgr = vals['PW_USE_PWD_MGR'];
            this.tlUseUniquePwd = vals['PW_USE_UNIQUE'];
            this.tlUseCase = vals['PW_USE_CASE'];
            this.tlUseLongLen = vals['PW_USE_LONG_LEN'];
            this.tlDoNotUse = vals['PW_DO_NOT_USE'];
            this.tlDoNotRepeat = vals['PW_DO_NOT_REPEAT'];
            this.tlDoNotUseUsernameEmail = vals['PW_DO_NOT_USE_USER_EMAIL'];
            this.tlDoNotUsePersonal = vals['PW_DO_NOT_USE_PERSONAL'];
            this.tlAvoid = vals['PW_AVOID'];
            this.tlAVoidSaving = vals['PW_AVOID_SAVING'];
            this.tlAvoidWriting = vals['PW_AVOID_WRITING'];
            this.tlAvoidSharing = vals['PW_AVOID_SHARING'];
            this.tlErrorProfile = vals['PW_ERROR_PROFILE'];
            this.tlErrorRecent = vals['PW_ERROR_RECENT'];
            this.tlUnknownError = vals['ERROR_OCCURRED_UNKNOWN'];
          });
  }

  isLowerCase(str) {
      return str === str.toLowerCase() && str !== str.toUpperCase();
  }

  isUpperCase(str) {
      return str === str.toUpperCase() && str !== str.toLowerCase();
  }

  ngOnInit(): void {
    this.passwordForm = this.formBuilder.group(
      {
        currentPassword: ['', Validators.required],
        password: [null, Validators.compose([
            // 1. Password Field is Required
            Validators.required,

            // 2. has 3 categories
            CustomValidators.has3Categories(this),

            // 3. Has a minimum length of 10 bytes
            CustomValidators.minByteLength(10),
            // 4. Has a maximum length of 50 bytes
            CustomValidators.maxByteLength(50),

            // 5. Has no | symbol
            CustomValidators.hasNoPipeSymbol()
        ]),
      ],
        confirmPassword: [null, Validators.compose([Validators.required])],
      },
      {
        validator: [
          CustomValidators.passwordMatchValidator('password', 'confirmPassword'),
          CustomValidators.isPasswordSimilar('currentPassword', 'password'),
          CustomValidators.isEmailOrSimilarFound('password', this.email)
        ]
      }
    );

    // Don't check when the mode is 'INVITATION' because it will redirect
    // the user to the login when it gets a 401 from the API.
    if (this.mode === 'UPDATE') {
      this.user$.subscribe(
        (user: IUser) => {
          const signInSource = authHelper.getSourceFromToken();
          const isCigentUser = signInSource === UserSource.Cigent;
          this.forcePassword = isCigentUser && user?.force_password;
        },
        () => {
            // Should this report an error?  I don't know.
            this.notification.showError(this.tlUnknownError);
        }
      );
    } else {
      this.p.currentPassword.disable();
    }
  }

  get p() {
    return this.passwordForm.controls;
  }

  getSpecialChars() {
      // @ts-ignore
    return IPasswordComponentHelper.specialCharacters.split('');
  }


  showPasswordError(errorType) {
    return IPasswordComponentHelper.showPasswordError(errorType, this.p);
  }

  onUpdatePassword() {
    this.submittedPasswd = true;

    if (this.passwordForm.invalid) {
      return;
    }

    this.translate.get(['UPDATE_COMPLETE']).subscribe(vals => {
      this.userService.updatePassword(this.passwordForm.value.currentPassword, this.passwordForm.value.password)
        .subscribe(
            response => {
              this.showPasswordResult(response.status, () => {
                if (this.forcePassword) {
                  this.authService.logout();
                } else {
                  this.userService.refreshUser();
                  this.notification.showSuccess(vals['UPDATE_COMPLETE']);
                  this.passwordForm.reset();
                }
              });
            },
            error => {
                let message = this.tlErrorProfile;
                // If recent password change error message...
                if (error === 'Recent Password') {
                    message = this.tlErrorRecent;
                }
                this.notification.showError(message);
            });
    });
  }

  onResetPassword() {
    if (this.passwordForm.invalid) {
      return;
    }

    if (this.isEmailVerified) {
      this.userService.resetPassword(this.email, this.resetCode, this.passwordForm.value.password).subscribe({
        next: result => {
          this.showPasswordResult(result.status, () => {
            this.router.navigate(['login']);
          })
        }
      });
    }
  }

  showPasswordResult(status: PasswordResetStatus, onSuccess: Function) {
    switch (status) {
      case PasswordResetStatus.SUCCESS:
        onSuccess.bind(this)();
        break;
      case PasswordResetStatus.FAILURE:
        this.notification.showError(this.translate.instant('CURRENT_PASSWORD_INVALID'));
        break;
      case PasswordResetStatus.INVALID:
        this.notification.showError(this.translate.instant('ERROR_RESET_PWD_INVALID'));
        break;
      case PasswordResetStatus.RECENT_PASSWORD_REUSED:
        this.notification.showError(this.tlErrorRecent);
        break;
      default:
        this.notification.showError(this.translate.instant('ERROR_RESET_PWD_UNKNOWN'));
        break;
    }
  }

  onResetPasswordByInvitation() {
    if (this.passwordForm.invalid) {
      return;
    }

    const password = this.passwordForm.value.password;
    this.userService.resetPasswordByInvitation(this.email, this.invitationUUID, password).subscribe(
      () => {
        this.router.navigate(['login']);
      },
      () => {
        this.notification.showError(this.tlUnknownError);
      }
    );
  }

  onUpdatePasswordForDeviceLogin() {
      if (this.passwordForm.invalid) {
          return;
      }

      this.userService.changePasswordByUsername(this.username, this.password, this.passwordForm.value.password)
          .pipe(
              switchMap(result => {
                  if (this.secondFactor) {
                    return of(null);
                  } else if (result.status == PasswordResetStatus.SUCCESS) {
                    // This is to generate the indirect login record in the database.
                    return this.userService.userLogin(
                        this.username,
                        this.passwordForm.value.password,
                        this.secondFactor,
                        null,
                        this.deviceToken,
                        this.stayLoggedIn
                    );
                  }
                  this.showPasswordResult(result.status, () => {});
              })
          ).subscribe(
          res => {
              if (res === null && this.secondFactor) {
                  // Going to reset and back to login page just to change the route
                  // so the UI is updated from the change password to the login form.
                  this.router.navigateByUrl('/reset', {skipLocationChange: true})
                    .then(()=>this.router.navigate(['/login']));
              } else if (res.status === LoginStatus.SUCCESS) {
                  this.router.navigate(['/login-success']);
              } else {
                  this.notification.showError(this.tlUnknownError);
              }
          },
          error => {
            this.notification.showError(this.tlUnknownError);
          });
  }

  onCapsLockCurrentChanged(event: boolean) {
    this.capsOnCurrent = event && this.passwordCurrentInputIsFocused;
  }

  onCapsLockNewChanged(event: boolean) {
    this.capsOnNew = event && this.passwordInputIsFocused;
  }

  onCapsLockConfirmChanged(event: boolean) {
    this.capsOnConfirm = event && this.passwordConfirmInputIsFocused;
  }

  passwordFocus(id: string) {

    this.passwordCurrentInputIsFocused = false;
    this.passwordInputIsFocused = false;
    this.passwordConfirmInputIsFocused = false;
    switch (id) {
      case "currentPassInput":
        this.passwordCurrentInputIsFocused = true;
        break;
      case "newPassInput":
        this.passwordInputIsFocused = true;
        break;
      case "confirmPassInput":
        this.passwordConfirmInputIsFocused = true;
        break;
    }
  }

  passwordBlur() {
    this.passwordCurrentInputIsFocused = false;
    this.passwordInputIsFocused = false;
    this.passwordConfirmInputIsFocused = false;
  }
}
